import { NeonColors, neonPanel, neonText, neonButton } from '../neon-theme'
import { mountToAppShell } from '../appShell'

interface RoomRuntimeConfig {
  fieldRadius: number
  baseSpeed: number
  boostMultiplier: number
  botCount: number
  minStake: number
  maxStake: number
  maxDuration: number
  respawnTimeLimit: number
}

interface RoomEconomyWindow {
  elapsedMs: number
  botEntries: number
  botEntrySparks: number
  botDeaths: number
  botDeathSparks: number
  botEvacuations: number
  botEvacuationSparks: number
  playerPaidEntries: number
  playerPaidEntrySparks: number
  playerTrialEntries: number
  playerTrialEntrySparks: number
  playerRespawns: number
  playerRespawnRevenueSparks: number
  playerEvacuations: number
  playerEvacuationPayoutSparks: number
  playerEvacuationRemainderSparks: number
  boostDroppedSparks: number
  sessionRefundSparks: number
  netPoolDeltaSparks: number
}

interface RoomEconomyDiagnostics {
  virtualInfinitePoolMode: boolean
  virtualPoolStartSparks: number
  observedPoolSparks: number | null
  shadowPoolSparks: number | null
  shadowPoolDriftSparks: number | null
  effectivePoolSparks: number | null
  poolHealthScore: number
  mapLiquidityScore: number
  looseWorldSparks: number
  botCarriedSparks: number
  humanCarriedSparks: number
  roomHeldSparks: number
  averageRoomHeldSparks: number
  peakRoomHeldSparks: number
  avgVisibleSparksNearFreshSpawn: number
  avgVisibleSparksNearHumans: number
  dryMapMsCurrent: number
  stressedPoolMsCurrent: number
  observeOnlyRecommendations: string[]
  window: RoomEconomyWindow
  control: {
    targetBotPopulation: number
    affordableBotEntriesAboveReserve: number | null
    drynessPressure: number
    poolStressPressure: number
    extraBotPressure: number
    turnoverPressure: number
    appliedActions: string[]
    explanation: string
  }
}

interface RoomInfo {
  roomName: string
  playerCount: number
  botCount: number
  phase: string
  fieldRadius: number
  uptime: number
  runtimeConfig: RoomRuntimeConfig
  economyDiagnostics: RoomEconomyDiagnostics
}

const EDITABLE_PARAMS: Array<{ key: keyof RoomRuntimeConfig; label: string; type: 'number' }> = [
  { key: 'baseSpeed', label: 'Base Speed', type: 'number' },
  { key: 'boostMultiplier', label: 'Boost Multiplier', type: 'number' },
  { key: 'botCount', label: 'Target Bot Count', type: 'number' },
  { key: 'minStake', label: 'Min Stake', type: 'number' },
  { key: 'maxStake', label: 'Max Stake', type: 'number' },
  { key: 'maxDuration', label: 'Max Duration (s)', type: 'number' },
  { key: 'respawnTimeLimit', label: 'Respawn Limit (s)', type: 'number' },
]

const INPUT_STYLE = `
  background: ${NeonColors.bgDark};
  border: 1px solid ${NeonColors.textDim};
  color: ${NeonColors.textBright};
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.85rem;
  width: 100%;
  box-sizing: border-box;
`

function phaseBadgeColor(phase: string): string {
  switch (phase) {
    case 'PLAYING': return NeonColors.success
    case 'SHRINKING': return NeonColors.danger
    case 'WAITING': return NeonColors.textMuted
    default: return NeonColors.info
  }
}

export class AdminRoomManager {
  private container: HTMLDivElement
  private token: string
  private refreshInterval: ReturnType<typeof setInterval> | null = null
  private expandedRoom: string | null = null

  constructor(token: string) {
    this.token = token
    this.container = document.createElement('div')
    this.container.style.cssText = 'width: 100%;'
  }

  render(parent: HTMLElement): void {
    parent.appendChild(this.container)
    this.container.innerHTML = `<p style="color: ${NeonColors.textMuted}; text-align: center; padding: 40px 0;">Loading rooms...</p>`
    this.refresh()
    this.refreshInterval = setInterval(() => this.refresh(), 10000)
  }

  dispose(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
    this.container.remove()
  }

  private async refresh(): Promise<void> {
    const data = await this.fetchAdmin('/rooms')
    const rooms = (data ?? []) as RoomInfo[]
    this.renderContent(rooms)
  }

  private renderContent(rooms: RoomInfo[]): void {
    const roomCards = rooms.length === 0
      ? `<p style="color: ${NeonColors.textMuted}; text-align: center; padding: 20px 0;">No active rooms</p>`
      : rooms.map(room => this.renderRoomCard(room)).join('')

    this.container.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: 12px; margin-bottom: 20px;">
        <h3 style="margin: 0; font-size: 0.95rem; ${neonText(NeonColors.info, NeonColors.infoGlow)}">
          Active Rooms (${rooms.length})
        </h3>
        ${roomCards}
      </div>
    `

    this.bindRoomEvents(rooms)
  }

  private renderRoomCard(room: RoomInfo): string {
    const isExpanded = this.expandedRoom === room.roomName
    const phaseColor = phaseBadgeColor(room.phase)

    const summaryRow = `
      <div class="room-card-header" data-room="${room.roomName}" style="
        display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
        cursor: pointer; padding: 10px 14px;
      ">
        <span style="
          color: ${NeonColors.textBright}; font-weight: bold; font-size: 0.9rem;
          flex: 1; min-width: 100px;
        ">${room.roomName}</span>
        <span style="color: ${NeonColors.textMuted}; font-size: 0.8rem;">
          Players: <strong style="color: ${NeonColors.info};">${room.playerCount}</strong>
        </span>
        <span style="color: ${NeonColors.textMuted}; font-size: 0.8rem;">
          Bots: <strong style="color: ${NeonColors.portal};">${room.botCount}</strong>
        </span>
        <span style="
          background: ${phaseColor}22;
          color: ${phaseColor};
          border: 1px solid ${phaseColor}44;
          border-radius: 4px;
          padding: 2px 8px;
          font-size: 0.7rem;
          text-transform: uppercase;
        ">${room.phase}</span>
        <span style="color: ${NeonColors.textMuted}; font-size: 0.8rem;">
          R: ${room.fieldRadius}
        </span>
        <span style="
          color: ${NeonColors.textDim}; font-size: 0.75rem;
          transform: rotate(${isExpanded ? '180' : '0'}deg);
          transition: transform 0.2s;
        ">&#9660;</span>
      </div>
    `

    const expandedSection = isExpanded ? this.renderExpandedConfig(room) : ''

    return `
      <div style="
        ${neonPanel()}
        padding: 0;
        border: 1px solid ${isExpanded ? NeonColors.info + '44' : NeonColors.textDim + '33'};
        transition: border-color 0.2s;
      ">
        ${summaryRow}
        ${expandedSection}
      </div>
    `
  }

  private renderExpandedConfig(room: RoomInfo): string {
    const config = room.runtimeConfig
    const economy = room.economyDiagnostics
    const paramFields = EDITABLE_PARAMS.map(param => `
      <div style="display: flex; flex-direction: column; gap: 4px;">
        <label style="color: ${NeonColors.textMuted}; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px;">
          ${param.label}
        </label>
        <div style="
          ${INPUT_STYLE}
          display: flex;
          align-items: center;
          min-height: 34px;
          opacity: 0.92;
        ">${config[param.key]}</div>
      </div>
    `).join('')

    const diagnostics = `
      <div style="
        grid-column: 1 / -1;
        margin-top: 6px;
        padding-top: 12px;
        border-top: 1px solid ${NeonColors.textDim}22;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        gap: 10px;
      ">
        ${this.renderMetricCard('Bots Now / Target', `${room.botCount} / ${economy.control.targetBotPopulation}`)}
        ${this.renderMetricCard('Pool Mode', economy.virtualInfinitePoolMode ? `Virtual (${economy.virtualPoolStartSparks})` : 'Real')}
        ${this.renderMetricCard('Map Liquidity', `${economy.mapLiquidityScore}/10`)}
        ${this.renderMetricCard('Pool Health', `${economy.poolHealthScore}/10`)}
        ${this.renderMetricCard('Loose Sparks', String(economy.looseWorldSparks))}
        ${this.renderMetricCard('Room Held Sparks', String(economy.roomHeldSparks))}
        ${this.renderMetricCard('Avg / Peak Held', `${economy.averageRoomHeldSparks} / ${economy.peakRoomHeldSparks}`)}
        ${this.renderMetricCard('Observed / Shadow Pool', `${economy.observedPoolSparks ?? '-'} / ${economy.shadowPoolSparks ?? '-'}`)}
        ${this.renderMetricCard('Affordable Bot Entries', economy.control.affordableBotEntriesAboveReserve === null ? '-' : String(economy.control.affordableBotEntriesAboveReserve))}
        ${this.renderMetricCard('Window Delta', `${economy.window.netPoolDeltaSparks} sparks`)}
        ${this.renderMetricCard('Boost Leakage', `${economy.window.boostDroppedSparks} sparks`)}
        ${this.renderMetricCard('Dry Streak', `${Math.round(economy.dryMapMsCurrent / 1000)}s`)}
        ${this.renderMetricCard('Pool Stress Streak', `${Math.round(economy.stressedPoolMsCurrent / 1000)}s`)}
        <div style="grid-column: 1 / -1; color: ${NeonColors.textMuted}; font-size: 0.78rem; line-height: 1.45;">
          <strong style="color: ${NeonColors.textBright};">Controller:</strong>
          ${economy.control.explanation}
        </div>
        <div style="grid-column: 1 / -1; color: ${NeonColors.textMuted}; font-size: 0.78rem; line-height: 1.45;">
          <strong style="color: ${NeonColors.textBright};">Applied actions:</strong>
          ${economy.control.appliedActions.join(' | ')}
        </div>
        <div style="grid-column: 1 / -1; color: ${NeonColors.textMuted}; font-size: 0.78rem; line-height: 1.45;">
          <strong style="color: ${NeonColors.textBright};">Observer recommendations:</strong>
          ${economy.observeOnlyRecommendations.join(' | ')}
        </div>
      </div>
    `

    return `
      <div style="
        border-top: 1px solid ${NeonColors.textDim}33;
        padding: 14px;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: 10px;
      ">
        ${paramFields}
        ${diagnostics}
      </div>
    `
  }

  private renderMetricCard(label: string, value: string): string {
    return `
      <div style="
        background: ${NeonColors.bgDark};
        border: 1px solid ${NeonColors.textDim}22;
        border-radius: 8px;
        padding: 8px 10px;
        display: flex;
        flex-direction: column;
        gap: 4px;
      ">
        <span style="color: ${NeonColors.textMuted}; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.5px;">
          ${label}
        </span>
        <span style="color: ${NeonColors.textBright}; font-size: 0.88rem; font-weight: 700;">
          ${value}
        </span>
      </div>
    `
  }

  private bindRoomEvents(rooms: RoomInfo[]): void {
    // Toggle expand/collapse
    const headers = this.container.querySelectorAll('.room-card-header')
    headers.forEach(header => {
      header.addEventListener('click', () => {
        const roomName = (header as HTMLElement).dataset.room
        if (!roomName) return
        this.expandedRoom = this.expandedRoom === roomName ? null : roomName
        this.renderContent(rooms)
      })
    })
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    const existing = document.querySelector('.admin-toast')
    if (existing) existing.remove()

    const color = type === 'success' ? NeonColors.success : NeonColors.danger
    const toast = document.createElement('div')
    toast.className = 'admin-toast'
    toast.style.cssText = `
      position: absolute;
      bottom: 24px;
      right: 24px;
      background: ${NeonColors.bgDark};
      border: 1px solid ${color};
      color: ${color};
      border-radius: 8px;
      padding: 10px 20px;
      font-size: 0.85rem;
      box-shadow: 0 0 16px ${color}44;
      z-index: 9999;
      opacity: 0;
      transition: opacity 0.3s ease;
    `
    toast.textContent = message
    mountToAppShell(toast)

    requestAnimationFrame(() => {
      toast.style.opacity = '1'
    })

    setTimeout(() => {
      toast.style.opacity = '0'
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }

  private async fetchAdmin(path: string): Promise<unknown> {
    try {
      const resp = await fetch('/api/admin' + path, {
        headers: { Authorization: 'Bearer ' + this.token },
      })
      if (!resp.ok) return null
      const json = await resp.json()
      return json.data ?? null
    } catch {
      return null
    }
  }
}
